import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { FAQ, MembershipProduct, MembershipPromotions } from '@app/core/models/drupal8';
import { I18nService, ToolsService } from '@app/core/services';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { ContentProtectionService } from '@app/core/services/content-protection/content-protection.service';
import { CrispChatService } from '@app/core/services/crisp-chat/crisp-chat.service';
import { DeviceDetectService } from '@app/core/services/device-detect/device-detect.service';
import { CurrentSubscription, MembershipService } from '@app/core/services/membership/membership.service';
import { ModalComponent } from '@app/core/services/modal-manager/modal-component';
import { NavDataService } from '@app/core/services/nav-data/nav-data.service';
import { RolesService } from '@app/core/services/roles/roles.service';
import { SubscriptionPlansService } from '@app/core/services/subscription-plans/subscription-plans.service';
import { TrackingService } from '@app/core/services/tracking/tracking.service';
import { CircularSpinnerComponent } from '@app/shared/components/circular-spinner/circular-spinner.component';
import { FlatTabSelectorComponent } from '@app/shared/components/flat-tab-selector/flat-tab-selector.component';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { SharedModule } from '@app/shared/shared.module';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { CartProductAdd, CommerceService, ViewService } from '@makiwin/ngx-drupal8-rest';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, takeUntil } from 'rxjs/operators';
import {
  MembershipPayActionConfiguration,
  MembershipProductMobile,
  categoryTerms,
} from './membership-subscriptions.configurations.type';
import { environment } from '@env/environment';

// /**
//  * Interface for the data of the presentation table of the benefits of the membership plans.
//  */
// interface MembershipPresentationTableData {
//   benefits: { title: string; description: string | undefined }[];
//   memberships: { name: string; benefits: boolean[] }[];
// }

interface ApiResponse {
  plans: MembershipProduct[];
  promotion?: MembershipPromotions | [] | boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    NgbAccordionModule,
    TranslateModule,
    CircularSpinnerComponent,
    SharedModule,
    FormatCurrencyPipe,
    PipesModule,
    FlatTabSelectorComponent,
    RouterModule,
    LocalizeRouterModule,
  ],
  selector: 'membership-subscriptions',
  templateUrl: './membership-subscriptions.component.html',
  styleUrls: ['./membership-subscriptions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipSubscriptionsComponent extends ModalComponent implements OnInit, OnDestroy {
  /**
   * Define if the component is used in a modal.
   * True if the component is used in a modal.
   */
  @Input() isModal = true;

  /**
   * List of faq for the current membership plans.
   */
  protected readonly faq = signal<FAQ[] | null>(null);
  protected readonly showPromoLabel = signal<boolean>(false);
  protected currentSubscription?: CurrentSubscription;
  // /**
  //  * A signal that holds the data for the membership presentation table.
  //  * It can be either of type `MembershipPresentationTableData` or `undefined`.
  //  *
  //  * @protected
  //  */
  // protected presentationTableData = signal<MembershipPresentationTableData | undefined>(undefined);
  /**
   * A signal that holds an array of MembershipProductMobile objects.
   * This is used to manage the presentation table data for desktop view.
   */
  protected readonly presentationTableDataDesktop = signal<MembershipProductMobile[]>([]);
  /**
   * A signal that holds an array of MembershipProductMobile objects.
   * This is used to manage the presentation table data for mobile view.
   */
  protected readonly presentationTableDataMobile = signal<MembershipProductMobile[]>([]);
  protected readonly isKiterMembership = signal<boolean>(false);
  /**
   * A signal representing the current index of the selected tab.
   *
   * @protected
   * @type {number}
   */
  protected readonly tabIndex = signal(0);
  /**
   * List of membership plans.
   */
  protected membershipPlans$: Observable<MembershipProduct[]> | undefined;
  /**
   * Define if the component has error to load the mebership plans.
   */
  protected readonly hasError = signal<boolean>(false);
  /**
   * Define if the pay button is disabled.
   */
  protected readonly buttonPayDisabled = signal<boolean>(false);
  /**
   * Action for 'pay' button.
   */
  protected actionForPay: ((product: MembershipProduct) => void) | any;
  /**
   * A signal that holds an array of strings representing the tabs for the mobile view.
   * This is used to manage the state of the tabs in the membership subscriptions component.
   */
  protected readonly tabsForMobile = signal<string[]>([]);
  /**
   * List of membership plans.
   */
  private membershipPlans: MembershipProduct[] = [];
  /**
   * Services
   */
  private readonly unsubsribeAll: Subject<void> = new Subject<void>();
  private readonly contentProtectionService: ContentProtectionService = inject(ContentProtectionService);
  private readonly authSessionService: AuthSessionService = inject(AuthSessionService);
  private readonly crispChatService: CrispChatService = inject(CrispChatService);
  private readonly toolsService: ToolsService = inject(ToolsService);
  private readonly commerceService: CommerceService = inject(CommerceService);
  private readonly membershipService: MembershipService = inject(MembershipService);
  private readonly trackingService: TrackingService = inject(TrackingService);

  /**
   * Configuration for the action to have for 'pay' button.
   * The configuration is based by product id of the current memebrship plan and the role of the user.
   * If the configuration meets the initial condition, but not find the product id, the action will become
   * the default aciton to pay.
   */
  private payActionConfiguration: MembershipPayActionConfiguration[] = [
    // Condition for anonymous user.
    {
      condition: () => !this.authSessionService.isLogged,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          action: () => this.contentProtectionService.protectWithLogin('/kiter-membership', true).subscribe(),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.assistant],
          action: () => this.contentProtectionService.protectWithLogin('/assistant-renewal', true).subscribe(),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () => this.contentProtectionService.protectWithLogin('/instructor-renewal', true).subscribe(),
        },
      ],
    },
    // Kiter Condition.
    {
      condition: () =>
        this.rolesService.isKiter || (this.rolesService.isExpired && this.rolesService.previousRoleIsKiter()),
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.center],
          action: (product: MembershipProductMobile) => {
            this.pay(product);
          },
        },
        {
          productIds: [this.subscriptionPlansService.productIds.assistant],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3658' } }),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3657' } }),
        },
      ],
    },
    // Assistant Condition.
    {
      condition: () =>
        this.rolesService.isAssistant || (this.rolesService.isExpired && this.rolesService.previousRoleIsAssistant()),
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          disableButtonToPay: true,
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3657' } }),
        },
      ],
    },
    // Instructors Condition.
    {
      condition: () =>
        this.rolesService.isInstructor || (this.rolesService.isExpired && this.rolesService.previousRoleIsInstructor()),
      actions: [
        {
          productIds: [
            this.subscriptionPlansService.productIds.kiter,
            this.subscriptionPlansService.productIds.assistant,
          ],
          disableButtonToPay: true,
        },
      ],
    },
    // School Condition.
    {
      condition: () =>
        this.rolesService.isSchool ||
        this.rolesService.isSchoolExpxired ||
        this.rolesService.isSchoolPending ||
        this.rolesService.isSchoolSuspended,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          action: () => this.navDataService.navigate('/students/register'),
        },
        {
          productIds: [
            this.subscriptionPlansService.productIds.assistant,
            this.subscriptionPlansService.productIds.instructor,
          ],
          action: () => this.navDataService.navigate('/profile-old/school', false, { fragment: 'team' }),
        },
      ],
    },
    // Condition for expired member that doens't have any previous role setted.
    {
      condition: () => this.rolesService.isExpired || this.rolesService.hasNoRole,
      actions: [
        {
          productIds: [
            this.subscriptionPlansService.productIds.assistant,
            this.subscriptionPlansService.productIds.instructor,
            this.subscriptionPlansService.productIds.kiter,
          ],
          action: (product) => this.openCripsChat(product),
        },
      ],
    },
  ];

  constructor(
    protected rolesService: RolesService,
    protected navDataService: NavDataService,
    protected deviceDetectService: DeviceDetectService,
    private viewService: ViewService,
    private translate: TranslateService,
    private i18n: I18nService,
    private subscriptionPlansService: SubscriptionPlansService
  ) {
    super();
  }

  @Input() set productId(productId: string) {
    if (productId) {
      this.extraData = { productId };
      this.checkProductIdForIL2AndAbove();
      if (!this.authSessionService.isLogged) {
        this.setActionsForPay();
      }
      // this.loadData();
    }
  }

  /**
   * Sets up an IntersectionObserver to monitor the visibility of elements with the `membershipBox` template reference.
   * When an element becomes at least 50% visible, updates the `tabIndex` with the index attribute
   * of the intersecting element.
   *
   * @param list - A QueryList of ElementRef objects representing the elements to be observed.
   */
  @ViewChildren('membershipBox') protected set membershipBoxes(list: QueryList<ElementRef>) {
    const elements = list.toArray();
    if (!elements.length) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = elements
              .filter((section) => section.nativeElement.id === entry.target.id)?.[0]
              ?.nativeElement?.getAttribute('index');
            this.tabIndex.set(index);
            break;
          }
        }
      },
      { threshold: 0.5 } // Trigger when 50% of the section is visible
    );

    list.forEach((section) => observer.observe(section.nativeElement));
  }

  ngOnInit() {
    // if (!this.extraData) {
    this.checkProductIdForIL2AndAbove();
    this.membershipService.currentSubscription$.pipe(takeUntil(this.unsubsribeAll)).subscribe((currentSubscription) => {
      this.currentSubscription = currentSubscription;
      this.setActionsForPay();
    });
    if (this.extraData?.productId && !this.authSessionService.isLogged) {
      this.setActionsForPay();
    }
    this.i18n.changedSelectedLanguage$.pipe(takeUntil(this.unsubsribeAll)).subscribe(() => {
      this.loadData();
    });

    // }
  }

  ngOnDestroy(): void {
    this.unsubsribeAll.next();
    this.unsubsribeAll.complete();
  }

  protected generatePriceMonth(price: string | number): number {
    return Math.round((parseFloat(price.toString().replace(',', '.')) / 12) * 10) / 10;
  }

  /**
   * Handles the change of the tab index.
   *
   * This method updates the current tab index and scrolls the corresponding
   * membership element into view smoothly.
   *
   * @param index - The new tab index to set.
   */
  protected onTabIndexChange(index: number) {
    this.tabIndex.set(index);
    document.getElementById(`membership_${index}`)?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Open the page/modal for the payment of the choosen product.
   *
   * @param product
   * Product to pay.
   */
  protected pay(product: MembershipProductMobile) {
    if (product.isProcessing?.()) {
      return;
    }
    if (environment.needD7 && product.product_id.toString() !== this.subscriptionPlansService.productIds.kiter) {
      this.handleD7Redirect();
      return;
    }
    product.isProcessing?.set(true);
    const requestData: CartProductAdd[] = [
      {
        purchased_entity_type: 'commerce_product_variation',
        purchased_entity_id: product.variation_id,
        quantity: 1,
      },
    ];
    this.commerceService.addToCart(requestData).subscribe((data) => {
      // item added to cart
      product.isProcessing?.set(false);
      if (data.length > 0) {
        // WEB-5428: navigate away of checkout page to retrigger the modalpopup
        this.navDataService.navigate('/shop');
        setTimeout(() => {
          this.navDataService.navigate('/shop/checkout', false, {
            queryParams: { checkout: true, order_id: data[0].order_id, checkCart: true },
          });
          if (this.isModal) {
            this.dismiss();
          }
        }, 100);
      }
    });
  }

  /**
   * Get the faq for the current membership plans.
   */
  protected getFaq() {
    const indexCategory = this.extraData?.productId
      ? categoryTerms.findIndex((c) => c.productId === this.extraData.productId)
      : categoryTerms.findIndex((c) => this.rolesService.userHasRole(c.name, true));
    if (indexCategory === -1) {
      return;
    }
    this.viewService
      .get(`/${this.translate.currentLang.replace('en', '')}/api/v1/faq.json`, {
        args: [categoryTerms[indexCategory].tid],
      })
      .subscribe((data) => {
        this.faq.set(data);
      });
  }

  /**
   * Load the data for the membership plans views.
   */
  protected loadData(): void {
    this.hasError.set(false);
    const subscription = this.extraData?.productId
      ? this.subscriptionPlansService.getByProductId(this.extraData.productId)
      : this.subscriptionPlansService.get();
    this.membershipPlans$ = subscription.pipe(
      // tap((plans) => {
      //   if (this.handleD7Redirect()) {
      //     this.close();
      //     return;
      //   }
      //   this.membershipPlans = plans;
      // }),
      mergeMap((plans) => this.getPromotionDate(plans)),
      map((apiResponse: ApiResponse) => {
        this.membershipPlans = apiResponse.plans;
        this.isKiterMembership.set(apiResponse.plans.length > 0 && apiResponse.plans[0].product_id.toString() === '2');
        this.membershipPlans.forEach((plan) => {
          plan.field_benefits_export.forEach((benefit) => {
            const [title, body, link] = benefit.details.split('<br><br>');
            benefit.titleMobile = this.toolsService.stripHtmlTags(title ?? '');
            benefit.detailsMobile = this.toolsService.stripHtmlExcept(body, ['br']);
            benefit.link = this.toolsService
              .extractLinkFromHtml(link ?? '')
              ?.replace(/\bhttps:\/\/[^\s]*\.ikointl\.com\b/g, '');
            benefit.linkText = this.toolsService.stripHtmlTags(link ?? '');
          });
        });
        this.createPresentationTableData();
        if (apiResponse.promotion) {
          this.checkPromotions(apiResponse.promotion);
        }
        // this.selectProduct(this.membershipPlans[1]);
        if (!this.isModal) {
          this.navDataService.setPageTitle({ title: this.membershipPlans[0]?.product_title });
        } else {
          this.trackingService.trackingManuallyPage('Membership modal', this.membershipPlans[0]?.product_title);
        }
        return apiResponse.plans;
      }),
      catchError(() => {
        this.hasError.set(true);
        return of([]);
      }),
      shareReplay(1)
    );
    this.getFaq();
    // this.setActionsForPay();
  }

  /**
   * Set the action and enabled status for the pay button, based by the configuration defined in
   * payActionConfiguration property.
   */
  private setActionsForPay() {
    // WEB-2299: open crisp chat and send message if user already have subscription active
    if (!this.extraData?.productId && this.authSessionService.isLogged) {
      this.actionForPay = this.checkDowngradeUpgrade;
      return;
    }

    const productId = this.extraData?.productId;
    for (const configuration of this.payActionConfiguration) {
      if (configuration.condition()) {
        for (const action of configuration.actions) {
          if (action.productIds.includes(productId)) {
            if (action.disableButtonToPay) {
              this.buttonPayDisabled.set(true);
              return;
            }
            this.actionForPay = (product: MembershipProduct) => {
              (action.action as any)(product);
              if (this.isModal) {
                this.dismiss();
              }
            };
            return;
          }
        }
        this.actionForPay = this.checkDowngradeUpgrade;
        return;
      }
    }
  }

  /**
   * Get promotion date, if presente.
   */
  private getPromotionDate(memberShipPlans: MembershipProduct[]): Observable<ApiResponse> {
    return this.subscriptionPlansService.getPromotionDate().pipe(
      map((data: any) => {
        return { plans: memberShipPlans, promotion: data };
      }),
      catchError(() => of({ plans: memberShipPlans }))
    );
  }

  /**
   * Checks whether the given membership product represents a downgrade or upgrade
   * compared to the current subscription. If the product matches the current subscription,
   * it opens the Crips chat. Otherwise, it proceeds with the payment process.
   *
   * @param product - The membership product to evaluate for downgrade or upgrade.
   */
  private checkDowngradeUpgrade(product: MembershipProductMobile) {
    if (this.currentSubscription && this.currentSubscription.product_id === product.product_id.toString()) {
      if (!this.membershipService.canRenew()) {
        this.openCripsChat(product);
        return;
      }
    }
    this.pay(product);
  }

  /**
   * Opens a Crisp chat window with a pre-filled message related to the selected membership product.
   *
   * @param product - The membership product for which the chat is being opened.
   *                  Contains details such as the product title and membership plan.
   *
   * This method uses the `crispChatService` to open the chat and pre-fills the message
   * with a translated string that includes the membership product's title and plan.
   * After opening the chat, it closes the current modal or context.
   */
  private openCripsChat(product: MembershipProductMobile): void {
    if (environment.needD7 && product.product_id.toString() !== this.subscriptionPlansService.productIds.kiter) {
      this.handleD7Redirect();
      return;
    }
    this.crispChatService.openChat(
      this.translate.instant('message_change_membership_chat', {
        membership: `${product.product_title} ${product.field_membership_plan}`,
      })
    );
    this.close();
  }

  /**
   * Preprocess the membership plans to create the data for the presentation table of
   * the benefits of the membership plans.
   */
  private createPresentationTableData(): void {
    const presentationTableDataMobile: MembershipProductMobile[] = [];
    this.presentationTableDataMobile.set([]);
    this.tabsForMobile.set([]);
    this.membershipPlans.forEach((product: MembershipProduct, index: number) => {
      const productMobile = { ...product };
      productMobile.price = this.cleanPrice(productMobile.price).toString();
      productMobile.price_discount = this.cleanPrice(productMobile.price_discount).toString();
      productMobile.field_features_export = [
        ...product.field_features_export.filter((feature) => feature.included === '1'),
        ...product.field_features_export.filter((feature) => feature.included !== '1'),
      ];
      (productMobile as any).index = index;
      this.tabsForMobile().push(product.field_membership_plan.toUpperCase());
      (productMobile as MembershipProductMobile).isProcessing = signal(false);
      presentationTableDataMobile.push(productMobile as MembershipProductMobile);
    });
    this.presentationTableDataDesktop.set([...presentationTableDataMobile]);
    const indexPlus = presentationTableDataMobile.findIndex(
      (plan) => plan.field_membership_plan.toLocaleLowerCase() === 'plus'
    );
    this.presentationTableDataMobile.set([
      ...presentationTableDataMobile.splice(indexPlus, 1),
      ...presentationTableDataMobile,
    ]);
    this.tabIndex.set(this.presentationTableDataMobile()[0].index);
  }

  private checkPromotions(promotion: MembershipPromotions | [] | undefined | null | boolean) {
    this.showPromoLabel.set(false);
    if (!promotion || (promotion as [])?.length === 0) {
      return;
    }
    let hasDiscount = false;
    const currentPromotion: MembershipPromotions = promotion as MembershipPromotions;
    this.presentationTableDataMobile.update((memberships) =>
      memberships.map((membership) => {
        if (membership.product_id.toString() !== currentPromotion.product_id.toString()) {
          return membership;
        }
        if (!currentPromotion.products[membership.variation_id]) {
          return membership;
        }
        hasDiscount = true;
        const discount = parseFloat(currentPromotion.promotion_percentage);
        membership.discount = discount * 100;
        const price = parseFloat(membership.price.replace(',', '.'));
        membership.priceWithDiscount = Math.ceil((price - price * discount) * 100) / 100;
        return membership;
      })
    );
    this.showPromoLabel.set(hasDiscount);
  }

  /**
   * Checks if the current user meets specific conditions related to instructor-level subscriptions (IL2 and above).
   *
   * If the user is logged in, the selected product ID matches the instructor plan,
   * and the user can act as a shadow assistant or their previous role was Instructor L2 or above (even if expired),
   * the method removes the `productId` from `extraData`.
   *
   * This will allow to load the specicic membership plans for the user.
   *
   * @private
   */
  private checkProductIdForIL2AndAbove() {
    if (
      this.extraData?.productId === this.subscriptionPlansService.productIds.instructor &&
      this.authSessionService.isLogged &&
      (this.rolesService.canShadowAssistant ||
        (this.rolesService.isExpired && this.rolesService.previousRoleIsInstructorL2AndAbove()))
    ) {
      delete this.extraData.productId;
    }
  }

  /**
   * Handles the redirection to a specific URL based on the D7 navigation data.
   * Currently, it navigates directly to the roles and fees management page.
   * (WEB-5358)
   *
   * @private
   * @returns {boolean} Always returns true.
   */
  private handleD7Redirect() {
    this.navDataService.navigate('https://www.ikointl.com/roles-fees-management', true);
    this.close();
    return true;
  }

  /**
   * Cleans a price value by normalizing its decimal separator.
   *
   * If the price contains both a comma and a period (e.g., "1,234.56"), it removes the comma,
   * assuming the comma is a thousands separator. Otherwise, it replaces any comma with a period,
   * treating the comma as a decimal separator (e.g., "12,34" becomes "12.34").
   *
   * @param price - The price value as a string or number.
   * @returns The cleaned price string with a normalized decimal separator.
   */
  private cleanPrice(price: string | number): number {
    const stringPrice = price.toString();
    return parseFloat(
      stringPrice.includes(',') && stringPrice.includes('.')
        ? stringPrice.replace(',', '')
        : stringPrice.replace(',', '.')
    );
  }
}
